import axios from 'axios';

// Event emitter for authentication errors
class AuthEventEmitter {
    constructor() {
        this.listeners = [];
    }

    on(callback) {
        this.listeners.push(callback);
        return () => {
            this.listeners = this.listeners.filter(l => l !== callback);
        };
    }

    emit(data) {
        this.listeners.forEach(callback => callback(data));
    }
}

export const authEvents = new AuthEventEmitter();

// Helper to extract the JWT from the hanko cookie
const getSessionTokenFromCookie = () => {
    const match = document.cookie.match(/(^| )hanko=([^;]+)/);
    return match ? match[2] : null;
};

/**
 * True when the JWT's exp is in the past (30 s clock-skew allowance) or the
 * token can't be decoded. Used to tell a genuinely expired session apart from
 * the backend rejecting a still-valid token (server-side auth fault) — only
 * the former should log the user out; treating the latter as expiry destroys
 * a perfectly good session and strands the user in a login loop while the
 * backend misbehaves.
 */
export const isJwtExpired = (token) => {
    if (!token) return true;
    try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (!payload.exp) return true;
        return payload.exp * 1000 < Date.now() + 30000;
    } catch {
        return true;
    }
};

function addAuthorizationHeader(config) {
    const accessToken = getSessionTokenFromCookie();
    if (accessToken) {
        config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
}

function handleResponseError(error) {
    // Check if this is a 401 Unauthorized error
    if (error.response && error.response.status === 401) {
        const token = getSessionTokenFromCookie();
        if (!token) {
            // No session cookie at all - this call was never authenticated
            // (e.g. an anonymous page firing a call that requires auth).
            // There's nothing to expire and no session to tear down; just
            // let the error propagate instead of bouncing the visitor to
            // /login with a misleading "session expired" message.
            console.warn('401 with no session cookie present - not an expired session, ignoring');
        } else if (isJwtExpired(token)) {
            console.warn('Token expired or unauthorized - emitting auth error event');

            // Emit event so components can handle the redirect
            authEvents.emit({
                type: 'TOKEN_EXPIRED',
                message: 'Your session has expired. Please log in again.',
                status: 401
            });

            // Clear the token from cookie
            document.cookie = 'hanko=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
        } else {
            // The token is still valid → server-side auth fault. Keep the
            // session; let the error propagate to the caller.
            console.warn('Backend rejected a still-valid session token (401) - keeping session');
        }
    }

    // Handle blocked domain responses
    if (error.response && error.response.status === 403) {
        const data = error.response.data;
        if (data && data.code === 'EMAIL_DOMAIN_BLOCKED') {
            authEvents.emit({
                type: 'ACCESS_BLOCKED',
                message: data.message,
                title: data.title,
                supportEmail: data.supportEmail,
                status: 403,
                payload: data
            });
        }
    }

    return Promise.reject(error);
}

export function createApiClient(config) {
    const apiClient = axios.create({
        timeout: 180000, // Global 3-minute timeout for all requests
        ...config
    });
    apiClient.interceptors.request.use(addAuthorizationHeader);
    apiClient.interceptors.response.use(response => response, handleResponseError);
    return apiClient;
}
